/**
 * useZtnaSessionHeartbeat — mantém a sessão do broker ZTNA deslizando junto
 * com a atividade no ConsoleAI.
 *
 * Problema: os cookies de sessão do broker são escopados em
 * `.zt.console.amazonasinovare.com.br`; o SPA roda em
 * `console.amazonasinovare.com.br` (fora do escopo) e nunca envia esses
 * cookies. Resultado: o `refresh_token` do broker (4h sliding) morre por
 * inatividade mesmo com o usuário ativo no app → re-login surpresa no ZTNA.
 *
 * Fix: em atividade do usuário (throttle de 5min, alinhado ao slide da sessão
 * do ConsoleAI), dispara POST cross-origin `/_broker/auth/refresh` com
 * `credentials:'include'`. O browser envia o cookie `refresh_token` (mesmo
 * domínio do broker); o broker valida, re-busca identity no DB e rotaciona o
 * refresh por +4h. CORS do broker já libera a origem do console.
 *
 * Best-effort: qualquer falha é silenciosa — nunca quebra a navegação.
 * Gateado por feature flag `ztna_session_heartbeat_enabled` (default OFF).
 */
import { useEffect, useRef } from 'react'
import { useAuthStore } from '@/store/auth'
import { useFeatureFlag } from '@/hooks/useFeatureFlags'

const THROTTLE_MS = 5 * 60 * 1000 // 5 min — alinhado ao slide da sessão Postgres
const ACTIVITY_EVENTS = ['click', 'keydown', 'visibilitychange'] as const

export function useZtnaSessionHeartbeat(): void {
  const enabled = useFeatureFlag('ztna_session_heartbeat_enabled')
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated)
  const brokerUrl = useAuthStore((s) => s.user?.ztna_broker_url)
  const lastBeatRef = useRef(0)

  useEffect(() => {
    if (!enabled || !isAuthenticated || !brokerUrl) return

    const beat = () => {
      const now = Date.now()
      if (now - lastBeatRef.current < THROTTLE_MS) return
      lastBeatRef.current = now
      // Best-effort: não aguardamos nem tratamos a resposta (CORS impede leitura).
      void fetch(`${brokerUrl}/_broker/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      }).catch(() => {
        /* soft-fail: heartbeat nunca derruba a navegação */
      })
    }

    // Bate uma vez ao montar (sessão pode já estar perto de expirar).
    beat()

    const onActivity = () => {
      // visibilitychange dispara também ao ocultar a aba; só batemos ao voltar.
      if (document.visibilityState === 'hidden') return
      beat()
    }
    ACTIVITY_EVENTS.forEach((ev) => window.addEventListener(ev, onActivity, { passive: true }))
    return () => {
      ACTIVITY_EVENTS.forEach((ev) => window.removeEventListener(ev, onActivity))
    }
  }, [enabled, isAuthenticated, brokerUrl])
}
